<template>
  <section>
    <section>
      <z-form v-if="views.init" ref="form" @submit.prevent.native="handle_send_code">
        <template slot="default">
          <z-form-input
            ref="email"
            :rules="{ required: true, email: true }"
            name="Email"
            class="mb-5"
          >
            <label>
              {{ $t('core.reset_password.field.email') }}
            </label>
            <i-input v-model="form.email" :disabled="is_button_loading" />
          </z-form-input>

          <div class="mt-4">
            <i-button
              class="auth-content__form__submit"
              type="primary"
              :loading="is_button_loading"
              :disabled="is_button_loading"
              @click="handle_send_code"
            >
              {{ $t('core.reset_password.button.send_code') }}
            </i-button>
          </div>
        </template>
      </z-form>

      <z-form v-if="views.check_code" ref="form" class="mb-5" @submit.prevent.native="verify_code">
        <div class="reset-password-success my-3">
          {{ $t(message, { email: form.email }) }}
        </div>
        <z-form-group class="mb-2 row">
          <z-form-input class="col-12" :rules="{ min: 6, max: 6 }" role="presentation">
            <label>
              {{ $t('core.reset_password.field.code') }}
            </label>
            <i-input v-model="code" type="text" autocomplete="false" :disabled="is_code_verified">
              <template #suffix>
                <icon-suffix :is_code_verified="is_code_verified" />
                <icon-suffix
                  :is_running="is_timer_running"
                  :is_code_verified="is_code_verified"
                  :pretty_time="convertMinutesToHours"
                />
              </template>
            </i-input>
            <popup
              v-if="!is_code_verified && !views.user_ban"
              width="300px"
              top="27px"
              left="inherit"
              right="-290px"
            >
              <div
                style="text-indent: -4px"
                :class="{
                  link: true,
                  'link--disabled': views.disable_resending
                }"
                @click="handle_send_code"
              >
                {{ $t('core.reset_password.link.send_repeated') }}
              </div>
            </popup>
          </z-form-input>
        </z-form-group>
      </z-form>
      <new-passwords-form v-if="is_code_verified" :email="form.email" :code="code" />
      <link-to-auth-page />
    </section>
  </section>
</template>

<script>
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import LinkToAuthPage from './reset-password-link-to-auth-page';
  import { ResetPasswordService } from '@core/services/ResetPasswordService';
  import { EmailVerificationService } from '@core/services/EmailVerificationService';
  import Timer from './timer';
  import IconSuffix from './icon-suffix';
  import NewPasswordsForm from './new-passwords-form';
  import has from 'lodash-es/has';
  import Vue from 'vue';

  export default {
    components: { LinkToAuthPage, NewPasswordsForm, IconSuffix },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      views: {
        init: true,
        email_send: false,
        user_ban: false,
        disable_resending: false,
        code_exist: false,
        check_code: false,
        new_password: false
      },
      message: '',

      is_button_loading: false,
      code_input_disabled: false,
      is_code_verified: false,
      code: '',

      is_timer_running: false,
      timer: null,
      can_send_at_timestamp: 0,

      form: {
        email: ''
      }
    }),
    computed: {
      convertMinutesToHours() {
        let timestamp = this.can_send_at_timestamp;
        let hours = Math.floor(timestamp / 3600).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });
        let minutes = Math.floor((timestamp / 60) % 60).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });
        let seconds = (timestamp % 60).toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        });

        return `${hours}:${minutes}:${seconds}`;
      }
    },
    watch: {
      code: {
        handler: function (value) {
          if (value.replace(/ /g, '').length === 6 && !this.is_code_verified) {
            this.verify_code();
          }
        },
        immediate: true,
        deep: true
      }
    },
    created() {
      this.form.email = this.$route.params?.email || this.form.email;
    },
    destroyed() {
      this.reset();
    },
    methods: {
      handle_send_code() {
        if (!this.views.disable_resending) {
          this.check_last_sent();
        }
      },

      check_last_sent() {
        this.is_button_loading = true;
        ResetPasswordService.checkLastSent({ email: this.form.email })
          .then(({ has_new_status, can_send_at, is_banned }) => {
            this.views.init = false;
            this.views.check_code = true;

            if (is_banned) {
              this.views.user_ban = true;
              return (this.message = 'core.reset_password.day_block');
            }

            if (has_new_status) {
              const now_date = Math.round(new Date().getTime() / 1000);
              const can_send_at_date = can_send_at;
              if (now_date > can_send_at_date) {
                return this.send_email();
              } else {
                this.message = 'core.reset_password.code_exist';
                this.views.disable_resending = true;
                this.can_send_at_timestamp = can_send_at_date - now_date;
                return this.start();
              }
            } else if (!has_new_status) {
              return this.send_email();
            }
          })
          .finally(() => (this.is_button_loading = false));
      },

      send_email() {
        this.is_button_loading = true;

        this.views.email_send = false;
        this.views.user_ban = false;
        this.views.disable_resending = false;
        this.views.check_code = false;
        this.views.new_password = false;

        ResetPasswordService.send({ email: this.form.email })
          .then(() => {
            this.message = 'core.reset_password.success_send_code';
            this.views.check_code = true;
          })
          .catch((error) => {
            if (
              error?.response?.data?.errors?.email.filter(
                (i) => i.message === 'wait_attempts_time_interval'
              ).length > 0
            ) {
              this.views.user_ban = true;
              this.views.check_code = true;
              this.message = 'core.reset_password.day_block';
            } else {
              this.views.init = true;
              this.$noty.server_error(error);
            }
          })
          .finally(() => (this.is_button_loading = false));
      },

      verify_code() {
        if (this.code.replace(/ /g, '').length === 6) {
          EmailVerificationService.check({ email: this.form.email, code: this.code })
            .then((response) => {
              if (response?.success) {
                this.is_code_verified = true;
              } else {
                let server_error = this.$t(`core.reset_password.error.${response.errors}`);
                this.$noty.error(server_error);
              }
            })
            .catch((event) => {
              this.is_code_verified = false;
              
              const R = Vue.$R;
              const error_key = R.pathOr('', ['response', 'data', 'errors'], event);

              if (error_key === 'code_size') {
                this.$noty.error(this.$t('core.reset_password.error.code_size'));
              } else {
                this.$noty.server_error(event);
              }
            });
        }
      },

      start() {
        this.is_timer_running = true;
        if (!this.timer) {
          this.timer = setInterval(() => {
            if (this.can_send_at_timestamp > 0) {
              this.can_send_at_timestamp = this.can_send_at_timestamp - 1;
            } else {
              clearInterval(this.timer);
              this.reset();
            }
          }, 1000);
        }
      },
      stop() {
        this.is_timer_running = false;
        clearInterval(this.timer);
        this.timer = null;
      },
      reset() {
        this.stop();
        this.views.disable_resending = false;
        this.can_send_at_timestamp = 0;
        this.server_error = '';
      }
    }
  };
</script>
<style>
  .ivu-input-with-suffix {
    padding-right: 47px !important;
  }

  .ivu-input-suffix .timer {
    top: 10px;
  }
  .ivu-input-suffix .success-check {
    top: 2px;
  }
</style>
